import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Target, CheckCircle, XCircle } from 'lucide-react';
export function SelfDirectionPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1749">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1750">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1751">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1752">
            <span className="w-8 h-px bg-teal" data-id="element-1753"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1754">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1755"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1756">
            Self-Direction and Execution
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1757">
            At SyNeural, self-direction and execution refer to the ability to
            organize effort, follow through, maintain discipline, translate
            goals into action, and sustain progress over time without constant
            external pressure. It is the capability that turns intention into
            movement and potential into real output.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1758">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1759">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1760">
            This matters because many people have intelligence, creativity, or
            ambition without consistent execution. They may understand what
            should be done, but struggle to organize themselves, sustain
            momentum, or convert plans into results. Self-direction and
            execution therefore play a major role in whether potential becomes
            contribution, whether learning becomes action, and whether
            opportunity turns into progress.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1761">
        <div className="max-w-4xl mx-auto" data-id="element-1762">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1763">
            <div className="flex items-center gap-4 mb-6" data-id="element-1764">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1765">
                <Target size={24} data-id="element-1766" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1767">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1768">
              Without execution, many other strengths remain dormant. Weak
              self-direction can undermine education, work, leadership
              development, and personal stability even when talent is present.
              Strong execution improves reliability, momentum, confidence, and
              readiness for greater responsibility. It is one of the clearest
              bridges between capability and real-world performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1769">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1770">
          <div className="flex items-center gap-4 mb-6" data-id="element-1771">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1772">
              <CheckCircle size={24} data-id="element-1773" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1774">
              What Strong Self-Direction Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1775">
            People with strong self-direction and execution are more likely to
            set goals, create structure, follow through on commitments, manage
            time effectively, and keep moving even when motivation fluctuates.
            They do not depend entirely on pressure or supervision to act. They
            are better able to generate forward movement from within.
          </p>
        </motion.div>
      </section>

      {/* What Weak Self-Direction Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1776">
        <div className="max-w-4xl mx-auto" data-id="element-1777">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1778">
            <div className="flex items-center gap-4 mb-6" data-id="element-1779">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1780">
                <XCircle size={24} data-id="element-1781" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1782">
                What Weak Self-Direction Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1783">
              Weak self-direction often appears as inconsistency,
              procrastination, abandoned plans, chaotic routines, low
              follow-through, and difficulty converting ideas into sustained
              action. In many cases, the issue is not lack of ability but lack
              of structure, discipline, or reliable behavioral systems. Over
              time, this can create frustration, wasted talent, and
              underachievement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1784">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1785">
          <div className="flex items-center gap-4 mb-6" data-id="element-1786">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1787">
              <Compass size={24} data-id="element-1788" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1789">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1790">
            Self-direction and execution can be improved through habit systems,
            goal design, accountability structures, time management, coaching,
            milestone planning, and environments that reduce friction and
            support consistency. In the SyNeural framework, this capability is
            treated as crucial because judgment and talent only become
            meaningful when they can be translated into sustained action.
          </p>
        </motion.div>
      </section>
    </div>;
}